/**
 * DisaWidget — floating DISA avatar button with:
 *  - Dynamic greeting bubble (2s delay, 6s visible, fade-out)
 *  - Opens compact chat panel
 *  - Hidden on /disa full-page view
 */
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { tokenStore, progress as progressApi } from "@/lib/api";
import DisaChat from "./DisaChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISA_AVATAR =
  "/assets/images/disa-avatar.png";

// ── Session persistence ──────────────────────────────────────────────────────
const SESSION_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function sessionKey(uid) { return `disa_session_${uid}`; }

function readSession(uid) {
  try {
    const raw = localStorage.getItem(sessionKey(uid));
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (!d?.lastActivityAt || Date.now() - d.lastActivityAt > SESSION_TTL_MS) return null;
    return d; // { conversationId, messages, lastActivityAt }
  } catch { return null; }
}

function saveSession(uid, conversationId, messages) {
  try {
    localStorage.setItem(sessionKey(uid), JSON.stringify({
      conversationId,
      messages,
      lastActivityAt: Date.now(),
    }));
  } catch { /* ignore */ }
}

function clearSession(uid) {
  try { localStorage.removeItem(sessionKey(uid)); } catch { /* ignore */ }
}

// ── Streak milestone toasts ─────────────────────────────────────────────────
const STREAK_MILESTONES = [3, 7, 14, 30];
const MILESTONE_COPY = {
  3:  { title: "3-day streak!", body: "You're building a beautiful habit." },
  7:  { title: "One whole week!", body: "Keep going — you're doing great." },
  14: { title: "Two weeks strong!", body: "Your consistency is incredible." },
  30: { title: "30-day streak!", body: "You're a champion. Truly remarkable." },
};

function milestoneStorageKey(uid) { return `kp_streak_milestones_${uid}`; }

function getUnseenMilestone(uid, days) {
  if (!uid || days < 1) return null;
  try {
    const seen = JSON.parse(localStorage.getItem(milestoneStorageKey(uid)) || "[]");
    return STREAK_MILESTONES.filter((m) => days >= m && !seen.includes(m)).pop() || null;
  } catch { return null; }
}

function markMilestoneSeen(uid, milestone) {
  try {
    const key = milestoneStorageKey(uid);
    const seen = JSON.parse(localStorage.getItem(key) || "[]");
    if (!seen.includes(milestone)) localStorage.setItem(key, JSON.stringify([...seen, milestone]));
  } catch { /* ignore */ }
}

// Greeting messages. Index 1 uses the child's name dynamically.
const GREETINGS = [
  () => "Need a quick activity idea?",
  (name) => `How is ${name || "your child"} progressing?`,
  () => "Ready to log a new milestone?",
  () => "Got a developmental question?",
  () => "Let's make today a great day!",
];

export default function DisaWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(null);
  const [initialMessages, setInitialMessages] = useState([]);
  const latestMessagesRef = useRef([]); // tracks the latest messages for expand-to-full-page
  const [streakDays, setStreakDays] = useState(0);
  const [childName, setChildName] = useState("");
  const [greeting, setGreeting] = useState("");
  const [bubbleVisible, setBubbleVisible] = useState(false);
  const [bubbleMounted, setBubbleMounted] = useState(false);
  const [scrollHidden, setScrollHidden] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const timersRef = useRef([]);
  const scrollTimerRef = useRef(null);

  // Fetch child's first name once (for the personalized greeting)
  useEffect(() => {
    if (!user) return;
    const token = tokenStore.get();
    fetch(`${API}/children/me`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((d) => {
        const raw = (d?.name || "").trim().split(" ")[0];
        if (raw && raw.toLowerCase() !== "n/a") setChildName(raw);
      })
      .catch(() => {});
  }, [user]); // eslint-disable-line

  // Fetch activity streak once so the header chip stays current
  useEffect(() => {
    if (!user) return;
    progressApi.streak()
      .then((s) => {
        const days = s?.current_streak ?? s?.current_days ?? 0;
        setStreakDays(days);
        // Show a celebratory toast the first time each milestone is reached
        const milestone = getUnseenMilestone(user.id, days);
        if (milestone) {
          markMilestoneSeen(user.id, milestone);
          const { title, body } = MILESTONE_COPY[milestone];
          toast.success(`🔥 ${title}`, {
            description: body,
            duration: 6000,
          });
        }
      })
      .catch(() => {});
  }, [user?.id]); // eslint-disable-line

  // Show the greeting bubble on mount (2s in, 6s stay, then fade out)
  useEffect(() => {
    if (open || pathname === "/disa") return;

    // Pick a random message once childName resolves (or after a brief wait)
    const pickTimer = setTimeout(() => {
      const idx = Math.floor(Math.random() * GREETINGS.length);
      setGreeting(GREETINGS[idx](childName));

      // Mount the bubble (invisible)
      setBubbleMounted(true);
      // Fade it in on the next frame
      const fadeInTimer = setTimeout(() => setBubbleVisible(true), 50);
      // Start fade-out after 6s
      const fadeOutTimer = setTimeout(() => setBubbleVisible(false), 6050);
      // Unmount after fade-out completes (500ms transition)
      const unmountTimer = setTimeout(() => setBubbleMounted(false), 6600);

      timersRef.current = [fadeInTimer, fadeOutTimer, unmountTimer];
    }, 2000);

    timersRef.current.push(pickTimer);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [childName, pathname]); // eslint-disable-line

  // Hide bubble while scrolling; re-show gently after 1 s of stillness
  useEffect(() => {
    const onScroll = () => {
      setScrollHidden(true);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => setScrollHidden(false), 1000);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, []);

  const handleExpand = useCallback(() => {
    navigate("/disa", { state: { conversationId: convId, messages: latestMessagesRef.current } });
    setOpen(false);
  }, [navigate, convId]);

  // Read session from localStorage and restore state (called before opening)
  const restoreSession = useCallback(() => {
    if (!user?.id) return;
    const session = readSession(user.id);
    if (session) {
      setConvId(session.conversationId || null);
      setInitialMessages(session.messages || []);
      latestMessagesRef.current = session.messages || []; // seed ref so expand works immediately
    } else {
      setConvId(null);
      setInitialMessages([]);
      latestMessagesRef.current = [];
    }
  }, [user?.id]); // eslint-disable-line

  // Save/clear session when messages change in the widget (called by DisaChat)
  const handleMessagesChange = useCallback((msgs, cId) => {
    if (!user?.id) return;
    if (msgs.length === 0) {
      clearSession(user.id);
      setConvId(null); // reset so DisaChat doesn't reload the old conversation
      latestMessagesRef.current = [];
      return;
    }
    const clean = msgs.filter((m) => !m._streaming);
    if (clean.length > 0) {
      latestMessagesRef.current = clean; // keep ref up-to-date for expand navigation
      saveSession(user.id, cId || convId, clean);
    }
  }, [user?.id, convId]); // eslint-disable-line

  if (pathname === "/disa") return null;

  // Bottom position: guests sit at bottom-6 (no mobile nav bar);
  // logged-in users sit higher to clear the app's bottom nav (88px).
  const avatarBottom = user ? "bottom-[88px] md:bottom-6" : "bottom-6";
  const panelBottom  = user ? "bottom-[168px] md:bottom-24" : "bottom-[88px] md:bottom-24";

  return (
    <div
      className={`fixed ${avatarBottom} right-4 md:right-6 z-50 no-print flex flex-col items-end gap-2 ${user ? "kp-disa-safe-bottom" : ""}`}
      data-testid="disa-widget-container"
    >
      {/* ── Greeting bubble — floats above the avatar in flex column ── */}
      {bubbleMounted && !open && (
        <div
          data-testid="disa-greeting-bubble"
          style={{
            opacity: bubbleVisible && !scrollHidden ? 1 : 0,
            transition: scrollHidden
              ? "opacity 0.2s ease"       // fast hide while scrolling
              : "opacity 0.55s ease",     // gentle re-appear after stillness
            pointerEvents: bubbleVisible && !scrollHidden ? "auto" : "none",
          }}
          className="relative"
        >
          <div
            onClick={() => { restoreSession(); setOpen(true); }}
            className="
              bg-white dark:bg-gray-800
              text-gray-800 dark:text-gray-100
              text-xs font-medium leading-snug
              px-3 py-2.5 rounded-2xl rounded-br-sm
              shadow-lg border border-gray-100 dark:border-gray-700
              max-w-[180px] whitespace-nowrap cursor-pointer
              hover:shadow-xl transition-shadow
            "
          >
            {greeting}
          </div>
          {/* Speech bubble tail — pointing down-right toward avatar */}
          <div
            className="absolute top-full right-3 w-0 h-0"
            style={{
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "8px solid white",
            }}
          />
        </div>
      )}

      {/* ── Floating avatar button — always at bottom of flex column ── */}
      <button
        onClick={() => {
          setBubbleMounted(false);
          setBubbleVisible(false);
          if (!open) restoreSession(); // refresh session from localStorage before opening
          setOpen((v) => !v);
        }}
        aria-label={open ? "Close DISA chat" : "Open DISA chat"}
        data-testid="disa-widget-button"
        className={`
          w-11 h-11 rounded-full shadow-lg flex items-center justify-center
          transition-all duration-200 active:scale-95 overflow-visible shrink-0
          ${open
            ? "ring-2 ring-gray-400 ring-offset-1"
            : "ring-2 ring-teal-400 ring-offset-2"
          }
        `}
      >
        {open ? (
          <div className="w-full h-full rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
            <X className="w-5 h-5 text-white" />
          </div>
        ) : (
          <div className="relative w-full h-full">
            <img
              src={DISA_AVATAR}
              alt="DISA"
              className="w-full h-full object-cover rounded-full"
            />
            {/* Online indicator — top-right, double-ring pulse */}
            <span className="absolute -top-0.5 -right-0.5 flex h-3.5 w-3.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-green-500 border-2 border-white" />
            </span>
          </div>
        )}
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div
          data-testid="disa-widget-panel"
          className={`
            fixed z-50 no-print
            ${panelBottom} right-4
            md:right-6
            w-[calc(100vw-32px)] max-w-[360px]
            h-[480px]
            rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700
            overflow-hidden
            animate-in fade-in slide-in-from-bottom-4 duration-200
            ${user ? "kp-disa-panel-safe-bottom" : ""}
          `}
        >
          <DisaChat
            compact
            onClose={() => setOpen(false)}
            onExpand={handleExpand}
            conversationId={convId}
            onConversationId={setConvId}
            initialMessages={initialMessages}
            onMessagesChange={handleMessagesChange}
            streakDays={streakDays}
          />
        </div>
      )}
    </div>
  );
}
